// 空間関数チートシート（旧 buffer スキルを統合）。
//
// 役割: DuckDB spatial の主要関数を用途別に 1 行要約で網羅し、エージェントの「存在しない関数の
//       捏造」を防ぎ正しい関数を選ばせる。メートル/面積/距離系の CRS 取り扱いは共通規約
//       （spatial-core）の三段構えを前提とする。代表的な操作パターンとしてバッファを内包する。
// 関係: system-prompt.js が連結する。完全リファレンスは reference/duckdb_manual/spatial_funcitons.md。
export const SPATIAL_FUNCTIONS_SKILL = `## スキル: 空間関数チートシート

DuckDB spatial の主な関数を用途別にまとめる。**関数名は下記にあるものだけを使い、推測で関数を作らない。**
メートル/面積/距離/簡略化など平面距離に依存する操作は、必ず共通規約の CRS 三段構え（MakeValid → 投影 →
操作 → 逆投影）に従う。

### 計測
- \`ST_Area(geom)\` 面積（**投影座標系の単位**。4326 のままだと「平方度」になり無意味）。
- \`ST_Area_Spheroid(geom)\` 面積を m²（4326 前提・楕円体・低速。軸順は **[緯度, 経度]**）。
- \`ST_Length(geom)\` 線長（投影座標系の単位）。
- \`ST_Length_Spheroid(geom)\` 線長を m（4326 前提・楕円体・低速。軸順は **[緯度, 経度]**）。
- \`ST_Perimeter(geom)\` 周長（投影座標系の単位）。
- \`ST_Perimeter_Spheroid(geom)\` 周長を m（4326 前提・楕円体・低速。軸順は **[緯度, 経度]**）。
- \`ST_Distance(geom1, geom2)\` 平面距離（投影座標系の単位）。
- \`ST_Distance_Sphere(p1, p2)\` 2 点間の大円距離 m（POINT のみ・4326 前提・軸順 **[緯度, 経度]**）。
- \`ST_Distance_Spheroid(p1, p2)\` 2 点間の楕円体距離 m（POINT_2D・4326 前提・軸順 **[緯度, 経度]**）。
- \`ST_DWithin(geom1, geom2, d)\` 距離 d 以内か（d は座標系の単位。メートル判定は投影後に）。
- \`ST_DWithin_Spheroid(p1, p2, d)\` 2 点が d m 以内か（POINT_2D・4326 前提・軸順 **[緯度, 経度]**）。

### 述語（空間結合の条件・真偽）
- \`ST_Intersects\` 交差する / \`ST_Disjoint\` 交差しない。
- \`ST_Contains\` 内包する / \`ST_ContainsProperly\` 境界共有なしに内包する。
- \`ST_Within\` 内包される / \`ST_WithinProperly\` 境界共有なしに内包される。
- \`ST_Covers\` 覆う / \`ST_CoveredBy\` 覆われる（境界上の点も含めたいときに有用）。
- \`ST_Touches\` 接する / \`ST_Crosses\` 横切る / \`ST_Overlaps\` 重なる / \`ST_Equals\` 等しい。
- \`ST_Intersects_Extent\` bbox 同士が交差する（粗い事前フィルタ）。
- 空間結合は \`JOIN ... ON ST_Intersects(a.geom, b.geom)\` のように述語を ON 句に書く。

### オーバーレイ・集合演算
- \`ST_Intersection(a, b)\` 交差部分（クリップに使用）。
- \`ST_Union(a, b)\` 2 ジオメトリの和 / \`ST_Difference(a, b)\` 差分。
- \`ST_CoverageUnion(geoms[])\` 面ポリゴンカバレッジを結合（隣接面の集合向け）。
- \`ST_ConvexHull(geom)\` 凸包 / \`ST_ConcaveHull(geom, ratio, allowHoles)\` 凹包。

### 集計（GROUP BY と併用）
- \`ST_Union_Agg(geom)\` グループ内のジオメトリを融合（ディゾルブ。例: 属性ごとに境界を消す）。
- \`ST_MemUnion_Agg(geom)\` メモリ使用を抑えて union（大きなディゾルブ候補）。
- \`ST_Intersection_Agg(geom)\` グループ内ジオメトリの共通部分。
- \`ST_Extent_Agg(geom)\` 集合のバウンディングボックス（= \`ST_Envelope_Agg\`）。
- \`ST_Collect(list(geom))\` 行集合をマルチジオメトリにまとめる。
- \`ST_CoverageUnion_Agg\` / \`ST_CoverageSimplify_Agg\` / \`ST_CoverageInvalidEdges_Agg\` 面カバレッジの結合・簡略化・検査。
- 集計自体は 4326 のまま実行可。距離・面積を絡める場合のみ投影してから。
- ディゾルブは GIS の基本処理だが、実データでは微小な非ノード交差・重複点・簡略化由来の破綻で
  \`TopologyException: found non-noded intersection\` が出ることがある。下記のディゾルブ手順を優先する。

### 構築・編集
- \`ST_Buffer(geom, d)\` 半径 d のバッファ（d は座標系の単位。下記パターン参照）。
- \`ST_Centroid(geom)\` 重心 / \`ST_PointOnSurface(geom)\` 必ず面上に乗る代表点。
- \`ST_Simplify(geom, tol)\` 簡略化 / \`ST_SimplifyPreserveTopology(geom, tol)\` 位相保持簡略化。
- \`ST_CoverageSimplify(geoms[], tol)\` 面カバレッジの隙間・重なりを避けて簡略化。
- \`ST_MakeValid(geom)\` 不正ジオメトリの修復 / \`ST_Normalize(geom)\` 正規化。
- \`ST_ReducePrecision(geom, precision)\` 座標精度を丸める / \`ST_RemoveRepeatedPoints(geom, tolerance)\` 重複点削除。
- \`ST_Reverse(geom)\` 頂点順序を反転 / \`ST_Node(geom)\` 線を交点でノード化。
- \`ST_Boundary(geom)\` 境界 / \`ST_Envelope(geom)\` 最小外接矩形 / \`ST_MinimumRotatedRectangle(geom)\` 最小回転矩形。
- \`ST_MaximumInscribedCircle(geom)\` 最大内接円 / \`ST_VoronoiDiagram(geom)\` ボロノイ図。
- \`ST_Polygonize(geoms[])\` 線集合から面を生成。
- \`ST_MakePoint\` / \`ST_Point\` / \`ST_Point2D\` / \`ST_MakeLine\` / \`ST_MakePolygon\` / \`ST_MakeEnvelope(minx,miny,maxx,maxy)\`。
- \`ST_Multi(geom)\` 単一ジオメトリを multi 化 / \`ST_CollectionExtract(geom, type)\` コレクションから型別抽出。

### 線形参照・線操作
- \`ST_StartPoint(line)\` 始点 / \`ST_EndPoint(line)\` 終点 / \`ST_PointN(line, n)\` n 番目の頂点。
- \`ST_LineMerge(geom)\` LineString を結合 / \`ST_LineSubstring(line, start, end)\` 線の一部を抽出。
- \`ST_LineInterpolatePoint(line, fraction)\` 線上の補間点 / \`ST_LineInterpolatePoints(line, fraction)\` 複数補間点。
- \`ST_LineLocatePoint(line, point)\` 点に最も近い線上位置を 0..1 で返す。
- \`ST_ShortestLine(a, b)\` 2 ジオメトリ間の最短線。
- \`ST_LocateAlong(geom, m)\` / \`ST_LocateBetween(geom, m1, m2)\` M 値による抽出。

### アクセサ・変換・出力
- \`ST_Transform(geom, src, dst, true)\` CRS 変換（4326 を含むときは第 4 引数 \`true\` 必須）。
- \`ST_GeometryType(geom)\` 型名 / \`ST_Dimension(geom)\` 次元 / \`ST_NPoints(geom)\` 頂点数。
- \`ST_NumGeometries(geom)\` / \`ST_NGeometries(geom)\` コレクション要素数。
- \`ST_NumInteriorRings(poly)\` / \`ST_NInteriorRings(poly)\` ポリゴン内輪数。
- \`ST_X(geom)\` / \`ST_Y(geom)\` / \`ST_Z(geom)\` / \`ST_M(geom)\` 点の座標・M 値。
- \`ST_XMin/ST_XMax/ST_YMin/ST_YMax/ST_ZMin/ST_ZMax/ST_MMin/ST_MMax(geom)\` 範囲。
- \`ST_Extent(geom)\` BOX / \`ST_Extent_Approx(geom)\` 近似 BOX。
- \`ST_IsValid\` / \`ST_IsEmpty\` / \`ST_IsSimple\` / \`ST_IsClosed\` / \`ST_IsRing\` 状態確認。
- \`ST_HasZ\` / \`ST_HasM\` / \`ST_ZMFlag\` Z・M 次元の確認。
- \`ST_Dump(geom)\` コレクションを要素ジオメトリと path のリストに分解。
- \`ST_Points(geom)\` 全頂点を MULTIPOINT 化 / \`ST_ExteriorRing(poly)\` 外輪。
- \`ST_AsText(geom)\` WKT / \`ST_AsGeoJSON(geom)\` GeoJSON フラグメント / \`ST_AsWKB(geom)\` WKB / \`ST_AsHEXWKB(geom)\` HEXWKB。
- \`ST_GeomFromText(wkt)\` WKT 読込 / \`ST_GeomFromGeoJSON(json)\` GeoJSON fragment 読込 / \`ST_GeomFromWKB(blob)\` WKB 読込。
- \`ST_FlipCoordinates(geom)\` x↔y 入れ替え（軸順トラブルの対処）。

### タイル・ファイル・その他
- \`ST_TileEnvelope(z, x, y)\` XYZ タイル範囲 / \`ST_AsMVT\` MVT 生成 / \`ST_AsMVTGeom\` MVT 用クリップ。
- \`ST_QuadKey(lon, lat, level)\` quadkey / \`ST_Hilbert\` Hilbert 曲線 index。
- \`ST_Read(path)\` GDAL 経由で読み込み / \`ST_ReadSHP(path)\` Shapefile 読み込み / \`ST_ReadOSM(path)\` OSM PBF 読み込み。
- \`ST_Read_Meta(path)\` メタデータ / \`ST_Drivers()\` GDAL ドライバ一覧。
- \`ST_Affine\` / \`ST_Translate\` / \`ST_Scale\` / \`ST_Rotate\` / \`ST_RotateX/Y/Z\` / \`ST_TransScale\` 幾何変換。
- \`DuckDB_Proj_Version()\` / \`DuckDB_PROJ_Compiled_Version()\` PROJ バージョン確認。

---

### 代表的な操作パターン: バッファ（緩衝域）

**トリガ**: 「〜から N メートル/キロのバッファ」「緩衝域」「N m 以内の範囲」など。

**手順**:
1. \`describe_layer\` で対象レイヤーのジオメトリ型と範囲（重心）を確認する。
2. バッファ半径と対象範囲から、共通規約の規則で metric CRS を決める（キロ単位なら UTM、大陸規模なら方位図法）。
3. 共通規約の三段構えで SQL を組み立てる。
4. \`run_spatial_sql\` に \`layer_name\`（例: "<元レイヤー>_buffer_5km"）を付けて materialize する。

**正しい SQL パターン**（5000m バッファ・イラン西部 EPSG:32639 の例）:
\`\`\`sql
SELECT ST_Transform(
         ST_Buffer(
           ST_Transform(ST_MakeValid(geom), 'EPSG:4326', 'EPSG:32639', true),
           5000),
         'EPSG:32639', 'EPSG:4326', true) AS geom,
       * EXCLUDE (geom)
FROM "<元レイヤー>"
\`\`\`
- 半径の単位はメートル（5km なら 5000）。
- 属性列は \`* EXCLUDE (geom)\` で引き継ぐ（必要な列だけ明示してもよい）。

**やってはいけないこと**:
- 経緯度のまま \`ST_Buffer(geom, 5000)\` を生成しない（5000 度になり破綻する）。
- EPSG:4326 を含む \`ST_Transform\` で第 4 引数 \`true\` を付け忘れない。

---

### 代表的な操作パターン: ディゾルブ（属性別の境界融合）

**トリガ**: 「都道府県単位にまとめる」「市区町村を県でディゾルブ」「同じ属性のポリゴンを結合」「境界を消す」など。

**基本方針**:
1. 同じ集約レベルの既存レイヤーがあるなら、それを直接使う。大きな市区町村データから毎回都道府県を作らない。
2. 行政界が必要ならgeoBoundariesから目的に合うADMレベルを直接取得する。
3. 実行する場合は、まず \`ST_IsValid\`・\`ST_NPoints\`・件数・集約キーの件数を確認する。
4. 入力を \`ST_ReducePrecision\`、\`ST_RemoveRepeatedPoints\`、\`ST_MakeValid\` でクリーンにしてから集約する。
5. 125,000 件をいきなり 47 件にするような大きな集約は避け、中間キーで段階集約する。

**安定寄りの SQL パターン**:
\`\`\`sql
WITH cleaned AS (
  SELECT
    pref,
    city_code,
    ST_MakeValid(
      ST_RemoveRepeatedPoints(
        ST_ReducePrecision(geom, 0.0001),
        0.0000001
      )
    ) AS geom
  FROM "<元レイヤー>"
  WHERE pref IS NOT NULL
),
municipalities AS (
  SELECT
    pref,
    city_code,
    ST_MakeValid(ST_MemUnion_Agg(geom)) AS geom
  FROM cleaned
  GROUP BY pref, city_code
)
SELECT
  pref,
  ST_MakeValid(ST_MemUnion_Agg(geom)) AS geom
FROM municipalities
GROUP BY pref
\`\`\`

**関数選択**:
- まず \`ST_MemUnion_Agg\` を使う。遅いが大きなディゾルブでメモリ面が安定しやすい。
- 小さいデータなら \`ST_Union_Agg\` でもよい。
- \`ST_CoverageUnion_Agg\` は隣接面カバレッジ向けだが、破綻した入力ではプロセスが不安定になることがある。
  失敗ログがある場合はチャット内の汎用手段として繰り返さず、段階集約か事前生成へ切り替える。
- \`ST_ReducePrecision\` の値はデータの CRS に合わせる。EPSG:4326 なら \`0.0001\` は約 10m 級、\`0.001\` は約 100m 級。
  精度を落とすほど安定しやすいが形状は粗くなる。

**失敗時の判断**:
- \`TopologyException: found non-noded intersection\` が出たら、同じ \`ST_Union_Agg\` を少し変えて何度も試さない。
  精度丸め、重複点削除、\`ST_MakeValid\`、段階集約の順に切り替える。
- それでも失敗する大規模境界データは、fullではなくsimplifiedを取得するか、上位のADMレベルを使う。
- 代替として「集約前のポリゴンを属性つきで表示する」場合は、ユーザーに都道府県境界そのものではないことを明示する。`
